// The rows of a resume change card, and what applying some of them writes.
//
// The card used to offer all or nothing: one "Accept All to Resume" for three
// rewritten lines (founder, 2026-09-27: "I have no way to just approve
// selected one"). Now every change is its own row with its own tick, and
// applying writes exactly the ticked ones: a line left unticked keeps its old
// words, a new line left unticked is not added, a removal left unticked keeps
// the line. The server's apply-change is unchanged — it receives the same
// kind of proposal, just with the unticked parts put back.
//
// Pure functions, so the rules can be tested without a browser.
import { parseBulletsFromHtml, stripHtml } from '../../utils/resumeQuality';

const escapeHtml = (s = '') => String(s)
  .replace(/&/g, '&amp;')
  .replace(/</g, '&lt;')
  .replace(/>/g, '&gt;')
  .replace(/"/g, '&quot;');

// Where an entry keeps its lines: experience entries use `experience`,
// projects `description`, a few older shapes a `bullets` array.
const LINE_KEYS = ['experience', 'description', 'bullets'];
const lineKeyOf = (data = {}) => LINE_KEYS.find(k => data?.[k] !== undefined && data?.[k] !== null && data?.[k] !== '');

// Fields a change can make besides its lines, with the words a person uses.
const FIELD_LABELS = {
  position: 'Title', role: 'Role', companyName: 'Company', schoolName: 'School',
  degree: 'Degree', name: 'Name', dates: 'Dates', location: 'Location', category: 'Category',
};

// What the resume calls each section.
const SECTION_LABELS = {
  EXPERIENCE: 'Experience', PROJECTS: 'Projects', PROJECT: 'Projects', EDUCATION: 'Education',
  SKILLS: 'Skills', SUMMARY: 'Summary', SUMMARIES: 'Summary', CERTIFICATIONS: 'Certifications',
  AWARDS: 'Awards', PUBLICATIONS: 'Publications', VOLUNTEER: 'Volunteering', LANGUAGES: 'Languages',
};
export const sectionLabel = (type) => SECTION_LABELS[String(type || '').toUpperCase()]
  || (type ? String(type).charAt(0) + String(type).slice(1).toLowerCase() : '');

// Words of a piece of markup. Inline tags go without a trace ("Firebase
// <b>Auth</b>." is "Firebase Auth.", not "Firebase Auth ."), block ones
// become a space; the shared stripHtml then decodes and tidies.
export const textOf = (html = '') => stripHtml(String(html).replace(/<\/?(?:b|i|u|s|em|strong|a|span|mark|code|sub|sup|small)\b[^>]*>/gi, ''));

/**
 * The lines of an entry, each with its words (`text`) and the markup it is
 * stored with (`raw`), so a line kept as it was is written back exactly —
 * bold and links included — and only a line someone edits is re-escaped.
 */
export function linesOf(value) {
  if (value === undefined || value === null || value === '') return [];
  if (Array.isArray(value)) {
    return value.map(x => String(x).trim()).filter(Boolean).map(t => ({ text: textOf(t) || t, raw: t, plain: true }));
  }
  const str = String(value);
  const items = [...str.matchAll(/<li[^>]*>([\s\S]*?)<\/li>/gi)];
  if (items.length) {
    return items.map(m => ({ text: textOf(m[1]), raw: m[1].trim() })).filter(l => l.text);
  }
  return parseBulletsFromHtml(str).map(t => ({ text: t, raw: escapeHtml(t) }));
}

// Words without the punctuation that ends them: "dashboards." is "dashboards",
// while "node.js" and "99.9%" stay whole. A trailing full stop used to keep an
// edited line from pairing with its old self.
const wordsOf = (s) => String(s || '').toLowerCase().match(/[a-z0-9][a-z0-9%$+#/-]*(?:\.[a-z0-9%]+)*/g) || [];
/** How alike two lines are, 0..1, by the words they share. */
export function likeness(a, b) {
  const A = new Set(wordsOf(a));
  const B = new Set(wordsOf(b));
  if (!A.size || !B.size) return 0;
  let shared = 0;
  A.forEach(w => { if (B.has(w)) shared += 1; });
  return (2 * shared) / (A.size + B.size);
}
const SAME_LINE = 0.34;

/**
 * Pairs the old lines with the new ones. Identical lines anchor first (so a
 * line inserted in the middle does not shift every line after it into a
 * "change"); between anchors, a new line takes the place of the old line it
 * most resembles, and a line that resembles nothing was added or removed.
 * The old card paired lines by position, so "Wrote runbooks for on-call"
 * showed as having become "Shipped an LLM-based search assistant".
 */
export function alignLines(oldLines = [], newLines = []) {
  const n = oldLines.length;
  const m = newLines.length;
  const L = Array.from({ length: n + 1 }, () => new Array(m + 1).fill(0));
  for (let i = n - 1; i >= 0; i -= 1) {
    for (let j = m - 1; j >= 0; j -= 1) {
      L[i][j] = oldLines[i].text === newLines[j].text ? L[i + 1][j + 1] + 1 : Math.max(L[i + 1][j], L[i][j + 1]);
    }
  }
  const anchors = [];
  for (let i = 0, j = 0; i < n && j < m;) {
    if (oldLines[i].text === newLines[j].text) { anchors.push([i, j]); i += 1; j += 1; } else if (L[i + 1][j] >= L[i][j + 1]) i += 1; else j += 1;
  }
  const rows = [];
  let oi = 0;
  let nj = 0;
  const gap = (oEnd, nEnd) => {
    const olds = oldLines.slice(oi, oEnd);
    const news = newLines.slice(nj, nEnd);
    let k = 0;
    for (const o of olds) {
      let best = -1;
      let bestScore = SAME_LINE;
      for (let t = k; t < news.length; t += 1) {
        const s = likeness(o.text, news[t].text);
        if (s > bestScore) { best = t; bestScore = s; }
      }
      if (best === -1) { rows.push({ kind: 'removed', old: o }); continue; }
      for (; k < best; k += 1) rows.push({ kind: 'added', new: news[k] });
      rows.push({ kind: 'modified', old: o, new: news[best] });
      k = best + 1;
    }
    for (; k < news.length; k += 1) rows.push({ kind: 'added', new: news[k] });
  };
  for (const [i, j] of anchors) {
    gap(i, j);
    rows.push({ kind: 'same', old: oldLines[i], new: newLines[j] });
    oi = i + 1;
    nj = j + 1;
  }
  gap(n, m);
  return rows;
}

const keyOfWord = (w) => w.toLowerCase().replace(/^[("'[]+/, '').replace(/[)"'\].,;:!?]+$/, '');
// Small words are marked only inside a changed phrase ("web app enabling"),
// never on their own: a lone highlighted "on" or struck "to" is noise.
const SMALL = new Set(['a', 'an', 'the', 'and', 'or', 'of', 'to', 'in', 'on', 'for', 'with', 'by', 'at', 'as',
  'from', 'into', 'via', 'per', 'is', 'are', 'was', 'were', 'be', 'it', 'its', 'their', 'our', 'your', 'that', 'this']);
/**
 * Which words are new and which were dropped, as marked-up parts of each
 * line. By vocabulary, not by position: a rewrite reorders words, and a
 * word that is still there somewhere is not news. A line that is mostly new
 * is shown plain — highlighting nearly every word says nothing.
 */
export function wordChanges(oldText = '', newText = '') {
  const oldWords = String(oldText).split(/(\s+)/);
  const newWords = String(newText).split(/(\s+)/);
  const oldKeys = new Set(oldWords.filter(w => w.trim()).map(keyOfWord).filter(Boolean));
  const newKeys = new Set(newWords.filter(w => w.trim()).map(keyOfWord).filter(Boolean));
  const mark = (words, others) => {
    const tokens = words.map(w => {
      const key = keyOfWord(w);
      return { w, isWord: Boolean(w.trim()), changed: Boolean(w.trim()) && Boolean(key) && !others.has(key), small: SMALL.has(key) };
    });
    const wordAt = tokens.map((t, i) => (t.isWord ? i : -1)).filter(i => i >= 0);
    wordAt.forEach((i, n) => {
      if (!tokens[i].small || !tokens[i].changed) return;
      const prev = tokens[wordAt[n - 1]];
      const next = tokens[wordAt[n + 1]];
      tokens[i].changed = Boolean(prev?.changed && !prev.small && next?.changed && !next.small);
    });
    const parts = [];
    let changedCount = 0;
    let total = 0;
    for (const t of tokens) {
      if (t.isWord) { total += 1; if (t.changed && !t.small) changedCount += 1; }
      const last = parts[parts.length - 1];
      // A space between two changed words stays inside the highlight, so
      // "platform giving" is one piece; a space after the last one does not.
      if (last && (last.changed === t.changed || !t.isWord)) last.text += t.w;
      else parts.push({ text: t.w, changed: t.changed });
    }
    // Trailing spaces move out of a highlight into the plain text after it.
    const tidy = [];
    for (const p of parts) {
      const m = p.changed ? p.text.match(/^([\s\S]*?\S)(\s+)$/) : null;
      if (m) { tidy.push({ text: m[1], changed: true }, { text: m[2], changed: false }); } else tidy.push(p);
    }
    const merged = [];
    for (const p of tidy) {
      const last = merged[merged.length - 1];
      if (last && last.changed === p.changed) last.text += p.text; else merged.push({ ...p });
    }
    return { parts: merged, share: total ? changedCount / total : 0 };
  };
  const was = mark(oldWords, newKeys);
  const now = mark(newWords, oldKeys);
  const plain = (text) => [{ text, changed: false }];
  const heavy = now.share > 0.6;
  return {
    oldParts: heavy ? plain(String(oldText)) : was.parts,
    newParts: heavy ? plain(String(newText)) : now.parts,
    heavy,
  };
}

const titleOf = (oldData = {}, newData = {}) => {
  const pick = (...keys) => {
    for (const k of keys) { const v = newData?.[k] || oldData?.[k]; if (v) return String(v).trim(); }
    return '';
  };
  const place = pick('companyName', 'schoolName', 'name');
  const role = pick('position', 'degree', 'role');
  return place && role ? `${place} · ${role}` : (place || role);
};

/**
 * Every change in a card as display units: one per resume entry touched, each
 * with its rows (the things that can be ticked) and its unchanged lines.
 * `lines` keeps the entry's lines in their order, each either an unchanged
 * line ({ same }) or a change ({ row: key }), so a review drawn in place shows
 * a change where it sits among the lines that stay.
 * Row keys are stable for a given proposal, so a tick survives re-renders and
 * new proposals arriving in the same turn.
 */
export function unitsOf(group = []) {
  const units = [];
  group.forEach((change, cIdx) => {
    const changeKey = String(change?.changeId ?? cIdx);
    const parts = change?.action === 'BATCH_UPDATE' && Array.isArray(change.updates)
      ? change.updates.map((u, uIdx) => ({ ...u, action: u.action || 'UPDATE', uIdx }))
      : [{ ...change, uIdx: null }];
    parts.forEach((part) => {
      const key = `${changeKey}|${part.uIdx === null ? '-' : part.uIdx}`;
      const oldData = part.oldData || {};
      const newData = part.newData || {};
      const section = sectionLabel(part.type);
      const isSummary = newData.content !== undefined || oldData.content !== undefined;
      const unit = {
        key, changeKey, uIdx: part.uIdx, type: part.type, entryId: part.entryId || null,
        action: part.action || 'UPDATE', section,
        title: isSummary ? 'Summary' : (titleOf(oldData, newData) || section || 'Resume entry'),
        rows: [], unchanged: [], lines: [],
      };
      const row = (id, fields) => unit.rows.push({ key: `${key}|${id}`, ...fields });

      if (unit.action === 'ADD') {
        const lines = linesOf(newData[lineKeyOf(newData)]);
        row('add', { kind: 'entry-add', label: 'New entry', newText: unit.title, lines: lines.map(l => l.text) });
      } else if (unit.action === 'DELETE') {
        row('delete', { kind: 'entry-delete', label: 'Remove entry', oldText: unit.title });
      } else if (isSummary) {
        const before = textOf(oldData.content || '');
        const after = textOf(newData.content || '');
        if (before !== after) row('summary', { kind: 'modified', oldText: before, newText: after, editable: true, field: 'content' });
      } else {
        for (const field of Object.keys(FIELD_LABELS)) {
          if (newData[field] === undefined || oldData[field] === undefined) continue;
          const before = String(oldData[field]).trim();
          const after = String(newData[field]).trim();
          if (before !== after) row(`field:${field}`, { kind: 'field', label: FIELD_LABELS[field], field, oldText: before, newText: after });
        }
        const lineKey = lineKeyOf(newData) || lineKeyOf(oldData);
        if (lineKey) {
          alignLines(linesOf(oldData[lineKey]), linesOf(newData[lineKey])).forEach((r, i) => {
            if (r.kind === 'same') { unit.unchanged.push(r.new.text); unit.lines.push({ same: r.new.text }); return; }
            row(`line:${i}`, {
              kind: r.kind, lineIndex: i,
              label: r.kind === 'added' ? 'New line' : r.kind === 'removed' ? 'Remove' : null,
              oldText: r.old?.text || null, newText: r.new?.text || null,
              editable: r.kind !== 'removed',
            });
            unit.lines.push({ row: `${key}|line:${i}` });
          });
        }
      }
      units.push(unit);
    });
  });
  return units;
}

/**
 * The proposals to send for a selection: each rebuilt so only its ticked rows
 * change anything. `isOn(rowKey)` says whether a row is ticked, `editOf(rowKey)`
 * gives words the person typed over a proposal (or undefined).
 *
 * Returns { apply, drop, kept, total, edited, leftOut } — `apply` goes to the
 * server, `drop` is proposals with nothing ticked (cleared, not written),
 * `leftOut` the words of the unticked rows, for the note to the assistant.
 */
export function resolveSelection(group = [], isOn = () => true, editOf = () => undefined) {
  const units = unitsOf(group);
  const byChange = new Map();
  units.forEach(u => {
    if (!byChange.has(u.changeKey)) byChange.set(u.changeKey, []);
    byChange.get(u.changeKey).push(u);
  });
  let kept = 0;
  let total = 0;
  let edited = 0;
  const leftOut = [];

  // One entry's proposal with only its ticked rows applied; null when none is.
  const settle = (unit, part) => {
    const on = unit.rows.filter(r => isOn(r.key));
    total += unit.rows.length;
    kept += on.length;
    unit.rows.filter(r => !isOn(r.key)).forEach(r => leftOut.push(r.newText || r.oldText || r.label || ''));
    const edits = unit.rows.filter(r => isOn(r.key) && typeof editOf(r.key) === 'string' && editOf(r.key).trim() && editOf(r.key).trim() !== r.newText);
    edited += edits.length;
    if (!on.length) return null;
    if (on.length === unit.rows.length && !edits.length) return part; // exactly as proposed

    const oldData = part.oldData || {};
    const newData = { ...(part.newData || {}) };
    for (const r of unit.rows) {
      if (r.kind === 'field' && !isOn(r.key)) newData[r.field] = oldData[r.field];
      if (r.field === 'content' && isOn(r.key)) {
        const text = editOf(r.key);
        if (typeof text === 'string' && text.trim()) newData.content = text.trim();
      }
    }
    const lineKey = lineKeyOf(part.newData) || lineKeyOf(oldData);
    if (lineKey) {
      const plainArray = Array.isArray(part.newData?.[lineKey]) || Array.isArray(oldData[lineKey]);
      const rows = alignLines(linesOf(oldData[lineKey]), linesOf(part.newData?.[lineKey]));
      const out = [];
      rows.forEach((r, i) => {
        const card = unit.rows.find(x => x.lineIndex === i);
        if (r.kind === 'same' || !card) { if (r.new) out.push(r.new); return; }
        const tick = isOn(card.key);
        const typed = editOf(card.key);
        const mine = typeof typed === 'string' && typed.trim() && typed.trim() !== r.new?.text
          ? { text: typed.trim(), raw: escapeHtml(typed.trim()) } : null;
        if (r.kind === 'modified') out.push(tick ? (mine || r.new) : r.old);
        else if (r.kind === 'added') { if (tick) out.push(mine || r.new); } else if (r.kind === 'removed') { if (!tick) out.push(r.old); }
      });
      newData[lineKey] = plainArray
        ? out.map(l => l.text)
        : (out.length ? `<ul>${out.map(l => `<li>${l.raw}</li>`).join('')}</ul>` : '');
    }
    return { ...part, newData };
  };

  const apply = [];
  const drop = [];
  group.forEach((change, cIdx) => {
    const changeKey = String(change?.changeId ?? cIdx);
    const mine = byChange.get(changeKey) || [];
    if (change?.action === 'BATCH_UPDATE' && Array.isArray(change.updates)) {
      const updates = change.updates
        .map((u, uIdx) => {
          const unit = mine.find(x => x.uIdx === uIdx);
          return unit ? settle(unit, u) : u;
        })
        .filter(Boolean);
      if (updates.length) apply.push(updates.length === change.updates.length && updates.every((u, i) => u === change.updates[i]) ? change : { ...change, updates });
      else drop.push(change);
      return;
    }
    const unit = mine[0];
    const settled = unit ? settle(unit, change) : change;
    if (settled) apply.push(settled); else drop.push(change);
  });
  return { apply, drop, kept, total, edited, leftOut: leftOut.filter(Boolean) };
}

/** The note that tells the assistant what the person kept, in their words. */
export function outcomeNote({ kept, total, edited, leftOut }) {
  const clip = (s) => (s.length > 90 ? `${s.slice(0, 87).trimEnd()}…` : s);
  if (kept === total && !edited) return null; // the plain "accepted" note says it
  const parts = [`(I applied ${kept} of the ${total} proposed change${total === 1 ? '' : 's'} to my resume; they are saved.`];
  if (edited) parts.push(`I edited ${edited === 1 ? 'one of them' : `${edited} of them`} before applying.`);
  if (leftOut.length) {
    parts.push(`I left out ${leftOut.length === 1 ? 'this one' : 'these'}, so ${leftOut.length === 1 ? 'it is' : 'they are'} not on my resume: ${leftOut.slice(0, 4).map(s => `"${clip(s)}"`).join('; ')}${leftOut.length > 4 ? '; and others' : ''}.`);
    parts.push('Do not describe the left-out ones as applied, and do not propose them again unless I ask.)');
  } else {
    parts[parts.length - 1] += ')';
  }
  return parts.join(' ');
}
